/**
 * The hosts that serve the webapp. A notification link on one of them is an
 * in-app route, not an outbound URL: the client rewrites it to a `history.push`
 * (`NotificationRouter`) and the admin validator reads the content it names.
 *
 * Shared so the two sides cannot drift — a host missing here makes the client
 * open the app in a new browser tab AND makes the validator wave the link
 * through, both silently.
 */
export const JUISCI_APP_HOSTS = [
  "app.juisci.com",
  "dev.juisci.com",
  "pro.juisci.com",
  "pro-dev.juisci.com",
] as const;

/**
 * The content a notification link opens, when that content is written in ONE
 * language. Playlists, articles and videos/infographics each exist as one row
 * per language (there is no translation group: `Medicina felina …` and
 * `Feline Medicine …` are two independent playlists), so the link itself picks
 * the language the reader lands in — whatever language the push copy was in.
 *
 * `null` from `parseNotificationLinkTarget` is the third, legitimate state:
 * an outbound URL, or an in-app route that carries no language of its own (a
 * room's discovery page, the personalized feed, the settings page).
 */
export type NotificationLinkTarget =
  | { kind: "playlist"; playlistId: string }
  | { kind: "article"; slug: string }
  | { kind: "content"; slug: string };

/**
 * The in-app path of a link, or `null` when it points somewhere else. A
 * relative link resolves against an app host, so `/room/playlist/…` and
 * `https://app.juisci.com/room/playlist/…` read the same.
 */
function inAppPath(link: string): string | null {
  const url = ((): URL | null => {
    try {
      return new URL(link, `https://${JUISCI_APP_HOSTS[0]}`);
    } catch {
      return null;
    }
  })();
  if (url === null) return null;
  const isAppHost = JUISCI_APP_HOSTS.some((host) => host === url.hostname);
  return isAppHost ? url.pathname : null;
}

/**
 * The content a notification link opens, or `null` when it opens nothing that
 * carries a language.
 *
 * The same route grammar is spelled again as regexes in
 * `router/front-serve/og-meta.ts` (which also covers podcasts and invites, and
 * wants them for a different reason: crawler meta). A new shareable content
 * route has to reach both.
 *
 * The route table mirrors `AppRoutes.tsx`, `ROOM_ROUTES` and the share links
 * `webapp/private/share-links.ts` mints (`/nectar/:slug`): every
 * language-scoped page exists twice, once public and once in-room
 * (`/post/:slug` and `/room/post/:roomId/:slug`). Each shape is matched
 * position by position rather than through a normalizing rewrite that strips
 * the room prefix — `/room/video/story/:roomId/:slug` puts the room id in the
 * FOURTH segment, and a rewrite that assumed the third would read the literal
 * "story" as a room id and the room id as a slug.
 */
export function parseNotificationLinkTarget(
  link: string,
): NotificationLinkTarget | null {
  const path = inAppPath(link);
  if (path === null) return null;
  const [first, second, third, fourth, fifth] = path
    .split("/")
    .filter((segment) => segment !== "");

  if (first === "room") {
    // `/room/playlist/:roomId/:id`
    if (second === "playlist" && fourth !== undefined)
      return { kind: "playlist", playlistId: fourth };
    // `/room/post/:roomId/:slug` (and its `/chat…` descendants)
    if (second === "post" && fourth !== undefined)
      return { kind: "article", slug: fourth };
    // `/room/video/story/:roomId/:slug` — before the plain video route below,
    // which would otherwise read the literal "story" as the room id.
    if (second === "video" && third === "story" && fifth !== undefined)
      return { kind: "content", slug: fifth };
    // `/room/video/:roomId/:slug`, `/room/infographic/:roomId/:slug`,
    // `/room/nectar/:roomId/:slug`
    if (
      (second === "video" || second === "infographic" || second === "nectar") &&
      fourth !== undefined
    )
      return { kind: "content", slug: fourth };
    return null;
  }

  // `/playlist/public/:id`
  if (first === "playlist" && second === "public" && third !== undefined)
    return { kind: "playlist", playlistId: third };
  // `/post/:slug` (and its `/chat…` descendants)
  if (first === "post" && second !== undefined)
    return { kind: "article", slug: second };
  // `/video/story/:slug` — before `/video/:slug`, same reason as in-room.
  if (first === "video" && second === "story" && third !== undefined)
    return { kind: "content", slug: third };
  if (
    (first === "video" || first === "infographic" || first === "nectar") &&
    second !== undefined
  )
    return { kind: "content", slug: second };

  return null;
}
